import { initMercadoPago } from '@mercadopago/sdk-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ResultadoPago, VistaPublica } from '../../../compartido/api';
import { fechaLarga, hhmm, soles, tramos } from '../../../compartido/formato';
import { FormularioTarjeta } from '../componentes/FormularioTarjeta';
import { Cruz, Escudo, Reloj } from '../componentes/Iconos';
import { AvisoError, CabeceraPaso, Cargando } from '../componentes/Piezas';
import { api, ErrorDeApi, mensajeDeError } from '../lib/api';
import { cargarConfig } from '../lib/config';
import { mmss } from '../lib/fechas';
import { useAhora, useCadaTanto, useEstable } from '../lib/ganchos';
import { explicarRechazo } from '../lib/rechazos';
import { correoDeReserva } from '../lib/sesion';

/**
 * Paso 3. El formulario de tarjeta es el Card Payment Brick de Mercado Pago:
 * los datos de la tarjeta nunca pasan por nuestro servidor, solo el token.
 *
 * Lo que puede pasar al pagar, y qué se muestra:
 *   CONFIRMADA   → a la pantalla de confirmación.
 *   RECHAZADO    → por qué, y probar con otra tarjeta si queda tiempo.
 *   DESAFIO      → la validación del banco (3DS) acá mismo, y se consulta el estado.
 *   VERIFICANDO  → se consulta el estado hasta que se resuelva. Nunca "paga de nuevo".
 */

let mpIniciado = false;

type Vista =
  | { tipo: 'formulario' }
  | { tipo: 'enviando' }
  | { tipo: 'rechazado'; detalle?: string; ultimos4?: string; puedeReintentar: boolean }
  | { tipo: 'desafio'; url: string }
  | { tipo: 'verificando' }
  | { tipo: 'sinRespuesta' };

/**
 * Si el formulario de Mercado Pago no aparece en este tiempo (bloqueador de
 * anuncios, red que no llega a mercadopago.com), se dice en vez de dejar un
 * hueco en blanco.
 */
const ESPERA_FORMULARIO_MS = 15_000;

/** Cuánto se consulta el estado antes de decir "te avisamos por correo". */
const CONSULTA_MS = 3000;
const CONSULTA_MAXIMA_MS = 3 * 60_000;

export function Pago() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const [reserva, setReserva] = useState<VistaPublica>();
  const [error, setError] = useState<string>();
  const [listoMP, setListoMP] = useState(mpIniciado);
  const [vista, setVista] = useState<Vista>({ tipo: 'formulario' });
  const [intentoForm, setIntentoForm] = useState(0);
  const [consultandoDesde, setConsultandoDesde] = useState<number>();
  const [formularioListo, setFormularioListo] = useState(false);
  const [formularioFallo, setFormularioFallo] = useState(false);
  // La página no se redibuja cada segundo: solo la cuenta regresiva lo hace
  // (CuentaRegresiva). Acá basta con enterarse una vez de que venció.
  const [vencioReloj, setVencioReloj] = useState(false);

  // Cada vez que se (re)monta el formulario, corre su plazo para aparecer.
  useEffect(() => {
    if (vista.tipo !== 'formulario') return;
    setFormularioListo(false);
    setFormularioFallo(false);
    const t = window.setTimeout(() => setFormularioFallo(true), ESPERA_FORMULARIO_MS);
    return () => window.clearTimeout(t);
  }, [vista.tipo, intentoForm]);

  const leer = useCallback(async () => {
    try {
      const r = await api.reserva(id);
      setReserva(r);
      return r;
    } catch (e) {
      setError(mensajeDeError(e));
      return undefined;
    }
  }, [id]);

  // Carga inicial: la reserva y la llave pública.
  useEffect(() => {
    void leer().then((r) => {
      if (r?.estado === 'CONFIRMADA') navegar(`/confirmada/${id}`, { replace: true });
      if (r?.estado === 'EN_COBRO') {
        setVista({ tipo: 'verificando' });
        setConsultandoDesde(Date.now());
      }
    });
    if (!mpIniciado) {
      cargarConfig().then(
        (c) => {
          if (!mpIniciado) initMercadoPago(c.publicKey, { locale: 'es-PE' });
          mpIniciado = true;
          setListoMP(true);
        },
        (e) => setError(mensajeDeError(e)),
      );
    }
  }, [id, leer, navegar]);

  // Mientras se espera al banco o al webhook, se consulta el estado.
  const consultando = consultandoDesde !== undefined;
  useCadaTanto(
    async () => {
      if (consultandoDesde !== undefined && Date.now() - consultandoDesde > CONSULTA_MAXIMA_MS) {
        setConsultandoDesde(undefined);
        setVista({ tipo: 'sinRespuesta' });
        return;
      }
      const r = await leer();
      if (!r) return;
      if (r.estado === 'CONFIRMADA') navegar(`/confirmada/${id}`, { replace: true });
      else if (r.estado === 'PENDIENTE_PAGO') {
        // Volvió a la retención: el cobro no entró.
        setConsultandoDesde(undefined);
        setVista({ tipo: 'rechazado', puedeReintentar: true });
      } else if (r.estado === 'EXPIRADA') {
        setConsultandoDesde(undefined);
        setVista({ tipo: 'formulario' });
      }
    },
    CONSULTA_MS,
    consultando,
  );

  const expiraEn = reserva?.estado === 'PENDIENTE_PAGO' ? reserva.expiraEn : undefined;
  useEffect(() => {
    setVencioReloj(false);
    if (!expiraEn) return;
    const falta = Date.parse(expiraEn) - Date.now();
    if (falta <= 0) {
      setVencioReloj(true);
      return;
    }
    const t = window.setTimeout(() => setVencioReloj(true), falta);
    return () => window.clearTimeout(t);
  }, [expiraEn]);
  const vencida = reserva?.estado === 'EXPIRADA' || (reserva?.estado === 'PENDIENTE_PAGO' && vencioReloj);

  const pagarConTarjeta = useEstable(async (...[datos, extra]: Parameters<Parameters<typeof FormularioTarjeta>[0]['onPagar']>) => {
    setError(undefined);
    setVista({ tipo: 'enviando' });
    try {
      const r = await api.pagar(id, {
        token: datos.token,
        metodoId: datos.payment_method_id,
        tipo: extra?.paymentTypeId ?? '',
        cuotas: datos.installments,
        // El Brick lo llama {type, number}; la API, {tipo, numero}.
        ...(datos.payer.identification?.number
          ? { documento: { tipo: datos.payer.identification.type, numero: datos.payer.identification.number } }
          : {}),
      });
      tratar(r, extra?.lastFourDigits);
    } catch (e) {
      if (e instanceof ErrorDeApi && e.estado === 0) {
        // Se cortó la conexión con el pago en vuelo: pudo haberse cobrado.
        // Se consulta el estado; nunca se invita a pagar de nuevo.
        tratar({ estado: 'VERIFICANDO' });
        return;
      }
      const r = await leer();
      if (r?.estado === 'CONFIRMADA') navegar(`/confirmada/${id}`, { replace: true });
      else if (r?.estado === 'EN_COBRO') tratar({ estado: 'VERIFICANDO' });
      else {
        setError(mensajeDeError(e));
        setVista({ tipo: 'formulario' });
        setIntentoForm((n) => n + 1);
      }
    }
  });
  const formularioAparecio = useEstable(() => setFormularioListo(true));
  const formularioFallo_ = useEstable((critico: boolean) => {
    if (critico) setError('No pudimos cargar el formulario de pago. Recarga la página.');
  });

  function tratar(r: ResultadoPago, ultimos4?: string) {
    switch (r.estado) {
      case 'CONFIRMADA':
        navegar(`/confirmada/${id}`, { replace: true });
        return;
      case 'RECHAZADO':
        setVista({ tipo: 'rechazado', ...(r.detalle ? { detalle: r.detalle } : {}), ...(ultimos4 ? { ultimos4 } : {}), puedeReintentar: r.puedeReintentar });
        void leer();
        return;
      case 'DESAFIO':
        setVista({ tipo: 'desafio', url: r.url });
        setConsultandoDesde(Date.now());
        return;
      case 'VERIFICANDO':
        setVista({ tipo: 'verificando' });
        setConsultandoDesde(Date.now());
        return;
    }
  }

  if (error && !reserva) {
    return (
      <div className="pantalla">
        <CabeceraPaso volverA="/" volverTexto="Volver al inicio" titulo="Pago" />
        <div className="contenido">
          <AvisoError>{error}</AvisoError>
          <Link to="/" className="boton">
            Elegir un horario
          </Link>
        </div>
      </div>
    );
  }
  if (!reserva) return <Cargando />;

  const cuenta = expiraEn && !vencida ? <CuentaRegresiva hasta={expiraEn} /> : undefined;

  const resumen = (
    <div className="tarjeta">
      <div className="fila">
        <span className="fuerte" style={{ fontSize: 14.5 }}>
          {fechaLarga(reserva.fecha).replace(/^./, (c) => c.toUpperCase())}
        </span>
      </div>
      <div className="chips">
        {reserva.horas.map((h) => (
          <span key={h} className="chip">
            {hhmm(h)}
          </span>
        ))}
      </div>
      <hr className="separador" />
      <div className="fila">
        <span className="nota">
          {reserva.horas.length} {reserva.horas.length === 1 ? 'hora' : 'horas'} × {soles(reserva.precio / reserva.horas.length)}
        </span>
        <span className="monto" style={{ fontSize: 23, color: 'var(--acento-texto)' }}>
          {soles(reserva.precio)}
        </span>
      </div>
    </div>
  );

  let cuerpo;
  if (vencida && vista.tipo === 'formulario') {
    cuerpo = (
      <>
        <AvisoError>Pasaron los 15 minutos y tus horas se liberaron. No se te cobró nada.</AvisoError>
        <Link to={`/dia/${reserva.fecha}`} className="boton">
          Volver a elegir horas
        </Link>
      </>
    );
  } else if (vista.tipo === 'rechazado') {
    const { encabezado, titulo, texto } = explicarRechazo(vista.detalle);
    const puede = vista.puedeReintentar && !vencida;
    cuerpo = (
      <>
        <div className="icono-redondo icono-redondo--error">
          <Cruz />
        </div>
        <div>
          <h2 className="titulo" style={{ fontSize: 28 }}>
            {encabezado}
          </h2>
          <p className="parrafo" style={{ marginTop: 10 }}>
            No se te cobró nada.
          </p>
        </div>
        <div className="tarjeta">
          <span className="chip" style={{ alignSelf: 'flex-start', background: '#fbeae7', color: '#8e3a30' }}>
            {titulo}
          </span>
          <p className="parrafo" style={{ fontSize: 13.5 }}>
            {vista.ultimos4 ? (
              <>
                Tarjeta terminada en <span className="fuerte">{vista.ultimos4}</span>. {texto}
              </>
            ) : (
              texto
            )}
          </p>
        </div>
        {puede ? (
          <div className="aviso aviso--calido">
            <Reloj color="#7A5A1E" />
            <div>
              <div className="fuerte" style={{ color: 'inherit' }}>
                Tu horario sigue reservado
              </div>
              {capital(fechaLarga(reserva.fecha))}, {tramos(reserva.horas)}. Te lo guardamos <strong>{expiraEn ? <Restante hasta={expiraEn} /> : '0:00'}</strong> más mientras
              resuelves el pago.
            </div>
          </div>
        ) : (
          <AvisoError>Ya no queda tiempo de retención: tus horas se liberaron. Vuelve a elegirlas.</AvisoError>
        )}
      </>
    );
  } else if (vista.tipo === 'desafio') {
    cuerpo = (
      <>
        <div>
          <h2 className="titulo" style={{ fontSize: 24 }}>
            Tu banco pide confirmar
          </h2>
          <p className="parrafo" style={{ marginTop: 8 }}>
            Completa la validación de tu banco. Tus horas están separadas mientras tanto.
          </p>
        </div>
        <iframe className="desafio" src={vista.url} title="Validación de tu banco" sandbox="allow-forms allow-scripts allow-same-origin allow-popups" />
      </>
    );
  } else if (vista.tipo === 'verificando' || vista.tipo === 'enviando') {
    cuerpo = (
      <>
        {resumen}
        <Cargando texto={vista.tipo === 'enviando' ? 'Procesando tu pago…' : 'Confirmando tu pago con el banco…'} />
        <p className="nota" style={{ textAlign: 'center' }}>
          No cierres esta página ni vuelvas a pagar. Tus horas están separadas.
        </p>
      </>
    );
  } else if (vista.tipo === 'sinRespuesta') {
    cuerpo = (
      <>
        {resumen}
        <div className="aviso aviso--calido" role="status">
          <Reloj color="#7A5A1E" />
          <span>
            Tu banco todavía no responde. Tus horas quedan separadas para ti y <strong>te escribimos apenas se confirme</strong>. No
            vuelvas a pagar.
          </span>
        </div>
      </>
    );
  } else {
    cuerpo = (
      <>
        {resumen}
        {error && <AvisoError>{error}</AvisoError>}
        {formularioFallo && !formularioListo && (
          <AvisoError>
            El formulario de pago no cargó. Si usas un bloqueador de anuncios, desactívalo para esta página y{' '}
            <button type="button" className="enlace-texto" style={{ padding: 0, color: 'inherit', textDecoration: 'underline' }} onClick={() => window.location.reload()}>
              recarga
            </button>
            . Tus horas siguen separadas.
          </AvisoError>
        )}
        {listoMP ? (
          <FormularioTarjeta
            key={intentoForm}
            precio={reserva.precio}
            correo={correoDeReserva(id)}
            onPagar={pagarConTarjeta}
            onListo={formularioAparecio}
            onFallo={formularioFallo_}
          />
        ) : (
          <Cargando texto="Cargando el formulario de pago…" />
        )}
        <div className="aviso">
          <Escudo />
          <span>El pago se procesa aquí mismo con Mercado Pago. Los datos de tu tarjeta no pasan por nuestros servidores.</span>
        </div>
      </>
    );
  }

  const pie =
    vista.tipo === 'rechazado' ? (
      <div className="pie">
        {vista.puedeReintentar && !vencida ? (
          <button
            type="button"
            className="boton"
            onClick={() => {
              setVista({ tipo: 'formulario' });
              setIntentoForm((n) => n + 1);
            }}
          >
            Probar con otra tarjeta
          </button>
        ) : (
          <Link to={`/dia/${reserva.fecha}`} className="boton">
            Volver a elegir horas
          </Link>
        )}
        <Link to="/" className="enlace-texto">
          Elegir otro horario
        </Link>
      </div>
    ) : undefined;

  return (
    <div className="pantalla">
      <CabeceraPaso volverA={`/dia/${reserva.fecha}`} volverTexto="Volver a elegir horas" antetitulo="Paso 3 de 3" titulo="Pago" derecha={cuenta} />
      <div className="contenido">{cuerpo}</div>
      {pie}
    </div>
  );
}

const capital = (s: string) => s[0]!.toUpperCase() + s.slice(1);

/** Solo esto se redibuja cada segundo; el resto de la página, no. */
function CuentaRegresiva({ hasta }: { hasta: string }) {
  const ahora = useAhora();
  const ms = Date.parse(hasta) - ahora.getTime();
  return (
    <span className="reloj" role="timer" aria-label={`Tiempo para pagar: ${mmss(ms)}`}>
      <Reloj tam={12} color="#9C4A34" />
      {mmss(ms)}
    </span>
  );
}

function Restante({ hasta }: { hasta: string }) {
  const ahora = useAhora();
  return <>{mmss(Date.parse(hasta) - ahora.getTime())}</>;
}
